/**
 * SecurityMain - Security settings UI for Level 1 admin.
 * First-time DEK setup, Reset DEK, and admin account recovery (recovery codes + 3 custom security questions).
 */

import React, { useState, useEffect } from 'react';
import { DekInput, validateDek } from './components/DekInput';
import { ResetDekModal } from './components/ResetDekModal';
import { DekInstructionsModal } from './components/DekInstructionsModal';
import { RecoveryInstructionsModal } from './components/RecoveryInstructionsModal';
import * as SecurityService from '../../services/SecurityService';
import './css/SecurityMain.css';

export const SecurityMain: React.FC = () => {
  const [dekSet, setDekSet] = useState(false);
  const [dekStatusLoading, setDekStatusLoading] = useState(true);
  const [showResetModal, setShowResetModal] = useState(false);
  const [setDekSubmitting, setSetDekSubmitting] = useState(false);
  const [resetDekSubmitting, setResetDekSubmitting] = useState(false);

  // First-time DEK form (admin password required to encrypt DEK)
  const [setDekPassword, setSetDekPassword] = useState('');
  const [part1, setPart1] = useState('');
  const [part2, setPart2] = useState('');
  const [part3, setPart3] = useState('');
  const [setDekError, setSetDekError] = useState('');
  const [resetDekError, setResetDekError] = useState<string | undefined>(undefined);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [showRecoveryInstructionsModal, setShowRecoveryInstructionsModal] = useState(false);

  // Admin account recovery (recovery codes + 3 custom questions)
  const [recoveryConfigured, setRecoveryConfigured] = useState(false);
  const [recoveryStatusLoading, setRecoveryStatusLoading] = useState(true);
  const [showRecoveryForm, setShowRecoveryForm] = useState(false);
  const [recoveryQuestion1, setRecoveryQuestion1] = useState('');
  const [recoveryQuestion2, setRecoveryQuestion2] = useState('');
  const [recoveryQuestion3, setRecoveryQuestion3] = useState('');
  const [recoveryId, setRecoveryId] = useState('');
  const [recoveryPin, setRecoveryPin] = useState('');
  const [recoveryAnswer1, setRecoveryAnswer1] = useState('');
  const [recoveryAnswer2, setRecoveryAnswer2] = useState('');
  const [recoveryAnswer3, setRecoveryAnswer3] = useState('');
  const [recoverySubmitting, setRecoverySubmitting] = useState(false);
  const [recoveryError, setRecoveryError] = useState('');

  useEffect(() => {
    SecurityService.getDekStatus()
      .then((res) => {
        if (res.success && res.dekSet !== undefined) setDekSet(res.dekSet);
      })
      .finally(() => setDekStatusLoading(false));
  }, []);

  useEffect(() => {
    SecurityService.getRecoveryStatus()
      .then((res) => {
        if (res.success && res.configured) {
          setRecoveryConfigured(true);
          if (res.question1) setRecoveryQuestion1(res.question1);
          if (res.question2) setRecoveryQuestion2(res.question2);
          if (res.question3) setRecoveryQuestion3(res.question3);
        }
      })
      .finally(() => setRecoveryStatusLoading(false));
  }, []);

  const handleSetDek = async (e: React.FormEvent) => {
    e.preventDefault();
    setSetDekError('');
    if (!setDekPassword.trim()) {
      setSetDekError('アカウントパスワードを入力してください。');
      return;
    }
    const value = part1 + part2 + part3;
    const err = validateDek(value);
    if (err) {
      setSetDekError(err);
      return;
    }
    setSetDekSubmitting(true);
    try {
      const res = await SecurityService.setDek(setDekPassword.trim(), part1, part2, part3);
      if (res.success) {
        setDekSet(true);
        setSetDekPassword('');
        setPart1('');
        setPart2('');
        setPart3('');
      } else {
        setSetDekError(res.message || '設定に失敗しました。');
      }
    } catch {
      setSetDekError('設定に失敗しました。');
    } finally {
      setSetDekSubmitting(false);
    }
  };

  const handleResetDekSubmit = async (payload: SecurityService.ResetDekPayload) => {
    setResetDekError(undefined);
    setResetDekSubmitting(true);
    try {
      const res = await SecurityService.resetDek(payload);
      if (res.success) {
        setShowResetModal(false);
      } else {
        setResetDekError(res.message || 'リセットに失敗しました。');
      }
    } catch {
      setResetDekError('リセットに失敗しました。');
    } finally {
      setResetDekSubmitting(false);
    }
  };

  const handleRecoverySetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setRecoveryError('');
    const q1 = recoveryQuestion1.trim();
    const q2 = recoveryQuestion2.trim();
    const q3 = recoveryQuestion3.trim();
    if (!recoveryId.trim()) {
      setRecoveryError('回復用IDを入力してください。');
      return;
    }
    if (!recoveryPin.trim()) {
      setRecoveryError('回復用PINを入力してください。');
      return;
    }
    if (!q1 || !q2 || !q3) {
      setRecoveryError('3つの質問をすべて入力してください。');
      return;
    }
    if (!recoveryAnswer1.trim() || !recoveryAnswer2.trim() || !recoveryAnswer3.trim()) {
      setRecoveryError('3つの回答をすべて入力してください。');
      return;
    }
    setRecoverySubmitting(true);
    try {
      const res = await SecurityService.saveRecoverySetup({
        recoveryId: recoveryId.trim(),
        recoveryPin: recoveryPin,
        question1: q1,
        question2: q2,
        question3: q3,
        answer1: recoveryAnswer1.trim(),
        answer2: recoveryAnswer2.trim(),
        answer3: recoveryAnswer3.trim(),
      });
      if (res.success) {
        setRecoveryConfigured(true);
        setShowRecoveryForm(false);
        setRecoveryPin('');
        setRecoveryAnswer1('');
        setRecoveryAnswer2('');
        setRecoveryAnswer3('');
      } else {
        setRecoveryError(res.message || '保存に失敗しました。');
      }
    } catch {
      setRecoveryError('保存に失敗しました。');
    } finally {
      setRecoverySubmitting(false);
    }
  };

  const openRecoveryForm = () => {
    setRecoveryError('');
    setRecoveryId('');
    setRecoveryPin('');
    setRecoveryAnswer1('');
    setRecoveryAnswer2('');
    setRecoveryAnswer3('');
    setShowRecoveryForm(true);
  };

  if (dekStatusLoading) {
    return (
      <div className="security-main main-content">
        <h3>セキュリティ設定</h3>
        <p className="security-loading">読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="security-main main-content">
      <h3>セキュリティ設定</h3>

      {!dekSet ? (
        <section className="security-section">
          <h4 className="security-section-title">マスターキー（DEK）の初回設定</h4>
          <p className="security-section-desc">
            本社従業員の給与データを暗号化するマスターキーを設定します。12文字を3つに分けて入力してください（大文字・小文字・数字・記号をそれぞれ1文字以上含めてください）。
          </p>
          <p className="security-instructions-link-wrap">
            <button
              type="button"
              className="security-instructions-link"
              onClick={() => setShowInstructionsModal(true)}
            >
              DEKの操作方法はこちら
            </button>
          </p>
          <form onSubmit={handleSetDek} className="security-set-dek-form">
            <div className="security-set-dek-field">
              <label htmlFor="security-set-dek-password" className="security-set-dek-label">現在のアカウントパスワード</label>
              <input
                id="security-set-dek-password"
                type="password"
                className="security-set-dek-input"
                value={setDekPassword}
                onChange={(e) => setSetDekPassword(e.target.value)}
                disabled={setDekSubmitting}
                placeholder="パスワードを入力"
                autoComplete="current-password"
              />
            </div>
            <DekInput
              part1={part1}
              part2={part2}
              part3={part3}
              onPart1Change={setPart1}
              onPart2Change={setPart2}
              onPart3Change={setPart3}
              error={setDekError}
              disabled={setDekSubmitting}
            />
            <div className="security-set-dek-actions">
              <button
                type="submit"
                className="security-btn security-btn-primary"
                disabled={setDekSubmitting}
              >
                {setDekSubmitting ? '設定中...' : 'マスターキーを設定する'}
              </button>
            </div>
          </form>
        </section>
      ) : (
        <section className="security-section">
          <h4 className="security-section-title">マスターキー（DEK）のリセット</h4>
          <p className="security-section-desc">
            マスターキーを変更する場合は、リセットを行ってください。リセット後は画面に表示される手順に従って操作してください。
          </p>
          <p className="security-instructions-link-wrap">
            <button
              type="button"
              className="security-instructions-link"
              onClick={() => setShowInstructionsModal(true)}
            >
              DEKの操作方法はこちら
            </button>
          </p>
          <div className="security-reset-actions">
            <button
              type="button"
              className="security-btn security-btn-secondary"
              onClick={() => setShowResetModal(true)}
            >
              マスターキーをリセットする
            </button>
          </div>
        </section>
      )}

      {/* Admin account recovery: recovery codes + 3 custom security questions */}
      <section className="security-section security-recovery-section">
        <h4 className="security-section-title">管理者アカウントの回復設定</h4>
        <p className="security-section-desc">
          パスワードを忘れた場合に、回復用ID・PINと秘密の質問3つで本人確認し、パスワードをリセットできます。回復用IDとPINはログイン画面で通常の従業員コード・パスワードの代わりに入力して回復フローを開始します。質問はご自身で自由に設定してください。
        </p>
        <p className="security-instructions-link-wrap">
          <button
            type="button"
            className="security-instructions-link"
            onClick={() => setShowRecoveryInstructionsModal(true)}
          >
            アカウント回復の設定方法はこちら
          </button>
        </p>
        {recoveryStatusLoading ? (
          <p className="security-loading">読み込み中...</p>
        ) : recoveryConfigured && !showRecoveryForm ? (
          <div className="security-recovery-configured">
            <p className="security-recovery-configured-text">回復設定は済んでいます。</p>
            <button
              type="button"
              className="security-btn security-btn-secondary"
              onClick={openRecoveryForm}
            >
              設定を変更する
            </button>
          </div>
        ) : (
          <form onSubmit={handleRecoverySetup} className="security-recovery-form">
            <div className="security-set-dek-field">
              <label htmlFor="recovery-id" className="security-set-dek-label">回復用ID（ログイン画面の従業員コード欄に入力）</label>
              <input
                id="recovery-id"
                type="text"
                className="security-set-dek-input"
                value={recoveryId}
                onChange={(e) => setRecoveryId(e.target.value)}
                disabled={recoverySubmitting}
                placeholder="例: RECOVERY01"
                autoComplete="off"
              />
            </div>
            <div className="security-set-dek-field">
              <label htmlFor="recovery-pin" className="security-set-dek-label">回復用PIN（ログイン画面のパスワード欄に入力）</label>
              <input
                id="recovery-pin"
                type="password"
                className="security-set-dek-input"
                value={recoveryPin}
                onChange={(e) => setRecoveryPin(e.target.value)}
                disabled={recoverySubmitting}
                placeholder="PINを入力"
                autoComplete="new-password"
              />
            </div>
            <div className="security-recovery-questions">
              <div className="security-set-dek-field">
                <label htmlFor="recovery-q1" className="security-set-dek-label">質問1</label>
                <input
                  id="recovery-q1"
                  type="text"
                  className="security-set-dek-input"
                  value={recoveryQuestion1}
                  onChange={(e) => setRecoveryQuestion1(e.target.value)}
                  disabled={recoverySubmitting}
                  placeholder="例: 母親の旧姓は？"
                  maxLength={500}
                />
                <input
                  type="text"
                  className="security-set-dek-input security-recovery-answer"
                  value={recoveryAnswer1}
                  onChange={(e) => setRecoveryAnswer1(e.target.value)}
                  disabled={recoverySubmitting}
                  placeholder="回答1"
                  autoComplete="off"
                />
              </div>
              <div className="security-set-dek-field">
                <label htmlFor="recovery-q2" className="security-set-dek-label">質問2</label>
                <input
                  id="recovery-q2"
                  type="text"
                  className="security-set-dek-input"
                  value={recoveryQuestion2}
                  onChange={(e) => setRecoveryQuestion2(e.target.value)}
                  disabled={recoverySubmitting}
                  placeholder="例: 初めて飼ったペットの名前は？"
                  maxLength={500}
                />
                <input
                  type="text"
                  className="security-set-dek-input security-recovery-answer"
                  value={recoveryAnswer2}
                  onChange={(e) => setRecoveryAnswer2(e.target.value)}
                  disabled={recoverySubmitting}
                  placeholder="回答2"
                  autoComplete="off"
                />
              </div>
              <div className="security-set-dek-field">
                <label htmlFor="recovery-q3" className="security-set-dek-label">質問3</label>
                <input
                  id="recovery-q3"
                  type="text"
                  className="security-set-dek-input"
                  value={recoveryQuestion3}
                  onChange={(e) => setRecoveryQuestion3(e.target.value)}
                  disabled={recoverySubmitting}
                  placeholder="例: 好きな映画のタイトルは？"
                  maxLength={500}
                />
                <input
                  type="text"
                  className="security-set-dek-input security-recovery-answer"
                  value={recoveryAnswer3}
                  onChange={(e) => setRecoveryAnswer3(e.target.value)}
                  disabled={recoverySubmitting}
                  placeholder="回答3"
                  autoComplete="off"
                />
              </div>
            </div>
            {recoveryError && <p className="security-recovery-error">{recoveryError}</p>}
            <div className="security-set-dek-actions security-recovery-actions">
              <button
                type="submit"
                className="security-btn security-btn-primary"
                disabled={recoverySubmitting}
              >
                {recoverySubmitting ? '保存中...' : recoveryConfigured ? '変更を保存する' : '回復設定を保存する'}
              </button>
              {recoveryConfigured && (
                <button
                  type="button"
                  className="security-btn security-btn-secondary"
                  onClick={() => setShowRecoveryForm(false)}
                  disabled={recoverySubmitting}
                >
                  キャンセル
                </button>
              )}
            </div>
          </form>
        )}
      </section>

      <ResetDekModal
        isOpen={showResetModal}
        onClose={() => { setShowResetModal(false); setResetDekError(undefined); }}
        onSubmit={handleResetDekSubmit}
        submitting={resetDekSubmitting}
        apiError={resetDekError}
      />
      <DekInstructionsModal
        isOpen={showInstructionsModal}
        onClose={() => setShowInstructionsModal(false)}
      />
      <RecoveryInstructionsModal
        isOpen={showRecoveryInstructionsModal}
        onClose={() => setShowRecoveryInstructionsModal(false)}
      />
    </div>
  );
};
